import React, { useEffect, useState } from "react";
import { X, Loader2, Save, Coins } from "lucide-react";
import DotCoinIcon from "./DotCoinIcon";

const RuleRow = ({ rule, client, onSaved }) => {
  const [amount, setAmount] = useState(rule.amount);
  const [enabled, setEnabled] = useState(rule.enabled);
  const [busy, setBusy] = useState(false);
  const dirty = Number(amount) !== rule.amount || enabled !== rule.enabled;
  const save = async () => {
    setBusy(true);
    try { onSaved((await client.put(`/admin/dotcoins/rules/${rule.key}`, { amount: Number(amount), enabled })).data); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <tr className="border-b border-slate-100" data-testid={`admin-dc-rule-${rule.key}`}>
      <td className="px-4 py-3"><div className="text-sm font-medium text-slate-800">{rule.label}</div><div className="text-xs text-slate-500">{rule.description}</div></td>
      <td className="px-4 py-3"><input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} className="w-20 rounded border border-slate-300 px-2 py-1 text-sm" data-testid={`admin-dc-rule-amount-${rule.key}`} /></td>
      <td className="px-4 py-3"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-[#14B8A6]" data-testid={`admin-dc-rule-enabled-${rule.key}`} /></td>
      <td className="px-4 py-3">
        <button disabled={!dirty || busy} onClick={save} className="inline-flex items-center gap-1 rounded bg-[#0F2A5F] px-2.5 py-1 text-xs text-white disabled:opacity-40" data-testid={`admin-dc-rule-save-${rule.key}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
        </button>
      </td>
    </tr>
  );
};

const AdjustForm = ({ doctors, client, onDone }) => {
  const [doctorId, setDoctorId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    try {
      const r = await client.post("/admin/dotcoins/adjust", { doctor_id: doctorId, amount: Number(amount), reason });
      setMsg(`Done. New balance: ${r.data.balance}`); setAmount(""); setReason(""); onDone();
    } catch (e2) { setMsg(e2?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-[2fr_1fr_2fr_auto]" data-testid="admin-dc-adjust-form">
      <select required value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="rounded border border-slate-300 px-2 py-2 text-sm" data-testid="admin-dc-adjust-doctor">
        <option value="">Select doctor…</option>
        {doctors.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.email}) — {d.balance}</option>)}
      </select>
      <input required type="number" placeholder="+/- amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded border border-slate-300 px-2 py-2 text-sm" data-testid="admin-dc-adjust-amount" />
      <input required placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded border border-slate-300 px-2 py-2 text-sm" data-testid="admin-dc-adjust-reason" />
      <button disabled={busy} className="rounded bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0F8A7D] disabled:opacity-50" data-testid="admin-dc-adjust-submit">Apply</button>
      {msg && <div className="text-xs text-slate-600 md:col-span-4" data-testid="admin-dc-adjust-message">{msg}</div>}
    </form>
  );
};

export const AdminDotCoins = ({ client, onClose }) => {
  const [rules, setRules] = useState([]);
  const [ov, setOv] = useState(null);
  const load = async () => {
    const [r, o] = await Promise.all([client.get("/admin/dotcoins/rules"), client.get("/admin/dotcoins/overview")]);
    setRules(r.data); setOv(o.data);
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);
  const onRuleSaved = (nr) => setRules((p) => p.map((x) => (x.key === nr.key ? nr : x)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="admin-dotcoins-modal">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
          <h3 className="flex items-center gap-2 font-bold text-slate-900"><DotCoinIcon size={22} /> DotCoins Management</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="admin-dotcoins-close"><X size={18} /></button>
        </div>
        {!ov ? <div className="p-10 text-center text-sm text-slate-400">Loading...</div> : (
          <div className="space-y-8 p-6">
            <div className="grid grid-cols-3 gap-4">
              {[["Total issued", ov.total_issued, "admin-dc-total-issued"], ["In circulation", ov.total_circulating, "admin-dc-circulating"], ["Active earners", ov.active_earners, "admin-dc-active-earners"]].map(([l, v, t]) => (
                <div key={l} className="rounded-lg border border-slate-200 p-4"><div className="text-xs uppercase tracking-wider text-slate-500">{l}</div><div className="mt-1 text-2xl font-bold text-[#0F2A5F]" data-testid={t}>{v}</div></div>
              ))}
            </div>
            <section>
              <h4 className="mb-2 text-sm font-semibold text-slate-800">Earning rules</h4>
              <table className="w-full rounded border border-slate-200">
                <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-2">Rule</th><th className="px-4 py-2">Coins</th><th className="px-4 py-2">Enabled</th><th className="px-4 py-2"></th></tr></thead>
                <tbody>{rules.map((r) => <RuleRow key={r.key + r.amount + r.enabled} rule={r} client={client} onSaved={onRuleSaved} />)}</tbody>
              </table>
            </section>
            <section>
              <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><Coins size={14} /> Manual adjustment</h4>
              <AdjustForm doctors={ov.doctors} client={client} onDone={load} />
            </section>
            <section>
              <h4 className="mb-2 text-sm font-semibold text-slate-800">Doctor balances</h4>
              <table className="w-full rounded border border-slate-200" data-testid="admin-dc-doctor-table">
                <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-2">Doctor</th><th className="px-4 py-2">Clinic</th><th className="px-4 py-2">Earned</th><th className="px-4 py-2">Balance</th><th className="px-4 py-2">Last activity</th></tr></thead>
                <tbody>
                  {ov.doctors.map((d) => (
                    <tr key={d.id} className="border-b border-slate-100 text-sm" data-testid={`admin-dc-doctor-${d.id}`}>
                      <td className="px-4 py-2"><div className="font-medium text-slate-800">{d.name}</div><div className="text-xs text-slate-500">{d.email}</div></td>
                      <td className="px-4 py-2 text-slate-600">{d.clinic_name || "-"}</td>
                      <td className="px-4 py-2 text-slate-600">{d.earned}</td>
                      <td className="px-4 py-2 font-semibold text-[#0F2A5F]">{d.balance}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{d.last_activity ? new Date(d.last_activity).toLocaleString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDotCoins;
